import { useState } from 'react';
import { History } from 'lucide-react';
import { SectionCard } from '@/components/ui/section-card';
import { Skeleton } from '@/components/ui/skeleton';
import { YearSelect } from '@/components/common/YearSelect';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { EmptyState } from '@/components/ui/empty-state';
import {
  useMyEventHistory,
  type EventHistoryEntry,
} from '@/api/extensions/useDashboardData';
import { formatCurrency, formatDate, getOrdinal } from '@/utils/formatters';
import { formatInTimezone, getDefaultTimezone } from '@/utils/timezone';

/**
 * The day the contestant competed.
 *
 * `date` is normally the performance's scheduled start — a UTC instant whose
 * wall-clock day belongs to the rodeo's timezone, not the viewer's. An 8pm
 * Mountain performance is stored as 02:00Z the NEXT day, so rendering it in
 * the browser's zone moves a Saturday run to Sunday. Pin it to the rodeo's
 * zone. When no performance time was scheduled the API falls back to the
 * rodeo's bare `YYYY-MM-DD` start date, which has no instant to convert.
 */
function formatPerformedOn(entry: EventHistoryEntry): string {
  if (!entry.date) return '--';
  if (/^\d{4}-\d{2}-\d{2}$/.test(entry.date)) return formatDate(entry.date) || '--';
  return (
    formatInTimezone(
      entry.date,
      entry.timezone || getDefaultTimezone(),
      { dateStyle: 'medium' },
      false,
    ) || '--'
  );
}

/**
 * Past results: every entry the contestant was scored in, where they placed,
 * and what they won — with the winnings totalled for the selected year.
 *
 * Winnings are THIS contestant's share. A team roping entry pays one prize to
 * two ropers, so the row shows their half and notes the team total beneath it;
 * the year total sums the shares, which is the money that actually reached
 * them.
 *
 * Collapsed by default, and the query is held behind that. Resolving
 * placements and prize money costs the API a full standings computation per
 * distinct event, which is real latency most visitors to the dashboard were
 * paying for a section they never looked at. Nothing is requested until
 * someone opens it.
 *
 * The list is unpaginated on purpose: a contestant's scored entries number in
 * the dozens, and a winnings total is only honest over the whole selection.
 *
 * Entry fees are deliberately absent — that's the money picture, and it lives
 * on Profile → Financials. Cancelled entries never reach this list; the API
 * filters to registrations with a live run, which is also what makes a DNP
 * (did-not-perform) row legitimate here: they were scored, they just didn't
 * compete, so they carry no placement.
 */
export function EventHistorySection() {
  const [open, setOpen] = useState(false);
  const [year, setYear] = useState<number | undefined>(undefined);

  const { data, isLoading } = useMyEventHistory({
    year,
    view: 'results',
    enabled: open,
  });

  const entries = data?.data ?? [];
  const totalWinnings = data?.totalWinnings ?? 0;

  return (
    <SectionCard
      title="Event History"
      collapsible
      open={open}
      onOpenChange={setOpen}
      headerRight={
        open ? (
          <YearSelect
            value={year}
            onChange={setYear}
            allowAll
            aria-label="Event history year"
            className="h-8 text-xs bg-white/10 border-white/20 text-inherit"
          />
        ) : undefined
      }
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} className="h-10 w-full" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState
          icon={History}
          title="No event history"
          description={
            year
              ? `No scored events found for ${year}.`
              : "Events you've competed in appear here once they've been scored."
          }
        />
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Event</TableHead>
                  <TableHead>Rodeo</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-right">Winnings</TableHead>
                  <TableHead className="text-right">Placement</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell className="font-medium">{entry.event_name}</TableCell>
                    <TableCell>
                      <span>{entry.rodeo_name ?? '--'}</span>
                      {entry.organization_name && (
                        <span className="block text-xs text-muted-foreground">
                          {entry.organization_name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{formatPerformedOn(entry)}</TableCell>
                    <TableCell
                      className={`text-right ${
                        (entry.winnings ?? 0) > 0 ? 'text-green-600 font-medium' : ''
                      }`}
                    >
                      {entry.winnings ? formatCurrency(entry.winnings) : '--'}
                      {entry.winnings && entry.team_winnings ? (
                        <span className="block text-xs font-normal text-muted-foreground">
                          {formatCurrency(entry.team_winnings)} team
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell className="text-right">
                      <PlacementCell entry={entry} />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="border-t-2 hover:bg-transparent">
                  <TableCell colSpan={3} className="font-medium">
                    {year ? `${year} total` : 'Total winnings'}
                    <span className="ml-2 text-xs font-normal text-muted-foreground">
                      {entries.length === 1 ? '1 event' : `${entries.length} events`}
                    </span>
                  </TableCell>
                  <TableCell
                    className={`text-right font-semibold ${
                      totalWinnings > 0 ? 'text-green-600' : ''
                    }`}
                  >
                    {formatCurrency(totalWinnings)}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {entries.map((entry) => (
              <div key={entry.id} className="rounded-lg border p-3 space-y-2">
                <div className="flex justify-between items-start gap-2">
                  <h4 className="font-medium text-sm">{entry.event_name}</h4>
                  <PlacementCell entry={entry} />
                </div>
                <p className="text-xs text-muted-foreground">
                  {[entry.rodeo_name, entry.organization_name, formatPerformedOn(entry)]
                    .filter(Boolean)
                    .join(' — ')}
                </p>
                {entry.winnings ? (
                  <div className="text-xs">
                    <span className="text-muted-foreground">Won: </span>
                    <span className="text-green-600 font-medium">
                      {formatCurrency(entry.winnings)}
                    </span>
                    {entry.team_winnings ? (
                      <span className="text-muted-foreground">
                        {' '}
                        ({formatCurrency(entry.team_winnings)} team)
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ))}
            <div className="flex items-center justify-between rounded-lg border-2 p-3 text-sm">
              <span className="font-medium">{year ? `${year} total` : 'Total winnings'}</span>
              <span className={`font-semibold ${totalWinnings > 0 ? 'text-green-600' : ''}`}>
                {formatCurrency(totalWinnings)}
              </span>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}

/**
 * Where they finished. A did-not-perform entry can't hold a placement — it is
 * called out as DNP instead of rendering an empty cell that reads as missing
 * data. "--" means unplaced, or results the organizer hasn't revealed yet.
 */
function PlacementCell({ entry }: { entry: EventHistoryEntry }) {
  if (entry.did_not_perform) {
    return (
      <span
        className="text-xs font-medium bg-muted text-muted-foreground px-2 py-0.5 rounded-full"
        title="Did not perform"
      >
        DNP
      </span>
    );
  }
  if (!entry.placement) return <span className="text-muted-foreground">--</span>;
  return (
    <span className="text-xs font-medium bg-primary/10 text-primary px-2 py-0.5 rounded-full">
      {getOrdinal(entry.placement)}
    </span>
  );
}
